import { collection, doc, setDoc, query, where, getDocs, getDoc, addDoc, orderBy } from 'firebase/firestore';
import { db } from '../config';
import { EmailCorrespondence } from '../../models/types';

/**
 * Add email correspondence record for a reservation
 */
export const addEmailCorrespondence = async (
  emailData: Omit<EmailCorrespondence, 'id'>
): Promise<string> => {
  try {
    const correspondenceRef = collection(db, 'reservations', emailData.reservationId, 'correspondence');
    const docRef = await addDoc(correspondenceRef, {
      ...emailData,
      timestamp: emailData.timestamp || new Date(),
      status: emailData.status || 'sent',
      opened: emailData.opened || false
    });
    return docRef.id;
  } catch (error) {
    console.error('Error adding email correspondence:', error);
    throw error;
  }
};

/**
 * Get all email correspondence for a reservation
 */
export const getEmailCorrespondenceForReservation = async (
  reservationId: string
): Promise<EmailCorrespondence[]> => {
  try {
    const correspondenceRef = collection(db, 'reservations', reservationId, 'correspondence');
    const q = query(correspondenceRef, orderBy('timestamp', 'desc'));
    
    const querySnapshot = await getDocs(q);
    return querySnapshot.docs.map(doc => {
      const data = doc.data();
      // Convert Firestore timestamp to Date
      const timestamp = data.timestamp?.toDate ? data.timestamp.toDate() : new Date(data.timestamp);
      const openedAt = data.openedAt?.toDate ? data.openedAt.toDate() : data.openedAt ? new Date(data.openedAt) : undefined;
      
      // Convert openEvents array timestamps
      const openEvents = data.openEvents ? data.openEvents.map((event: any) => ({
        timestamp: event.timestamp?.toDate ? event.timestamp.toDate() : new Date(event.timestamp),
        userAgent: event.userAgent || 'Unknown',
        ip: event.ip || 'Unknown',
        referrer: event.referrer || 'Direct'
      })) : [];
      
      return {
        id: doc.id,
        reservationId: data.reservationId,
        timestamp,
        subject: data.subject,
        body: data.body,
        recipient: data.recipient,
        sender: data.sender,
        template: data.template,
        opened: data.opened || false,
        openedAt,
        status: data.status,
        openEvents
      } as EmailCorrespondence;
    });
  } catch (error) {
    console.error('Error getting email correspondence:', error);
    throw error;
  }
};

/**
 * Update email opened status
 */
export const updateEmailOpenedStatus = async (
  reservationId: string,
  correspondenceId: string,
  opened: boolean = true
): Promise<void> => {
  try {
    const correspondenceRef = doc(db, 'reservations', reservationId, 'correspondence', correspondenceId);
    await setDoc(correspondenceRef, {
      opened,
      openedAt: opened ? new Date() : null
    }, { merge: true });
  } catch (error) {
    console.error('Error updating email opened status:', error);
    throw error;
  }
}; 